import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TAnnotatedDataPoint, TReportSection } from '@@config/report';
import StructuredGridTableRow from '@@components/StructuredGridTableRow/StructuredGridTableRow';

// StructuredGridTable Component
type TStructuredGridTableProps = {
    readonly section: TReportSection;
    readonly tableType?: 'default' | 'gemstone';
};

const StructuredGridTable: React.FC<TStructuredGridTableProps> = ({ section, tableType = 'default' }) => {
    const [currentTooltipIndex, setCurrentTooltipIndex] = useState<number>(-1);
    const tooltipInfoIconRef = useRef<HTMLButtonElement>(null);

    // Filter items that have both labels and tooltips for the tooltip functionality
    const itemsWithTooltips = section.ITEMS?.filter(item => item.LABEL && item.TOOLTIP) || [];
    const lastTooltipIndex: number = itemsWithTooltips.length - 1;
    const isTooltipTrackPlaying: boolean = currentTooltipIndex >= 0;

    function resetTooltips(): void {
        setCurrentTooltipIndex(-1);
        if (tooltipInfoIconRef.current) {
            tooltipInfoIconRef.current.focus();
        }
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            resetTooltips();
        }
    };

    function handleInfoIconClick(): void {
        if (!isTooltipTrackPlaying) {
            setCurrentTooltipIndex(0);
        } else {
            resetTooltips();
        }
    }

    function handleNextTooltipClick(): void {
        if (currentTooltipIndex === lastTooltipIndex) {
            resetTooltips();
        } else {
            setCurrentTooltipIndex(prevIndex => prevIndex + 1);
        }
    }

    function showTooltipbyIndex(index: number): void {
        setCurrentTooltipIndex(index);
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <>
            <div className={`xtable xtable-${tableType}`}>
                <div className='xtable-body relative'>
                    {itemsWithTooltips.length > 0 && (
                        <button
                            ref={tooltipInfoIconRef}
                            className='xtable-info absolute'
                            aria-label='More info'
                            onClick={handleInfoIconClick}
                        ></button>
                    )}

                    {section.ITEMS?.map((item: TAnnotatedDataPoint, index: number) => {
                        // Find the tooltip index for this item (only for items with labels and tooltips)
                        const tooltipIndex =
                            item.LABEL && item.TOOLTIP
                                ? itemsWithTooltips.findIndex(
                                      tooltipItem =>
                                          tooltipItem.KEY === item.KEY ||
                                          (tooltipItem.LABEL === item.LABEL && tooltipItem.VALUE === item.VALUE)
                                  )
                                : -1;

                        return (
                            <StructuredGridTableRow
                                key={item.KEY || `${item.LABEL}-${index}`}
                                item={item}
                                index={tooltipIndex}
                                currentTooltipIndex={currentTooltipIndex}
                                totalRows={itemsWithTooltips.length}
                                onNextTooltipClick={handleNextTooltipClick}
                                resetTooltips={resetTooltips}
                                showTooltipByIndex={showTooltipbyIndex}
                            />
                        );
                    })}
                </div>
            </div>
            {isTooltipTrackPlaying &&
                createPortal(
                    <div className='xtooltip-backdrop' onClick={() => resetTooltips()}></div>,
                    document.getElementById('report-check')!
                )}
        </>
    );
};

export type { TStructuredGridTableProps };
export default StructuredGridTable;
